import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Card, CardHeader, Col, Container, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import $ from 'jquery';
import 'datatables.net';
import 'datatables.net-bs5/css/dataTables.bootstrap5.min.css';
import 'datatables.net-bs5';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Breadcrumbs, H4 } from '../../../AbstractElements';
import { GainsTableHeader } from '../../../Constant';
import ManageEarnAdmin from '../../../api/ManageEarnAdmin';

const GAINS_CLIENT_ID = process.env.REACT_APP_GAINS_CLIENT_ID || '14';
const EARN_STATUSES = ['PENDING', 'VALIDATED', 'RECLAMED', 'WAITING_RECLAME'];

const earnStatusBadge = (status) => {
    switch (status) {
        case 'PENDING':          return '<span class="badge bg-warning text-dark">EN ATTENTE</span>';
        case 'VALIDATED':        return '<span class="badge bg-info text-dark">VALIDÉ</span>';
        case 'WAITING_RECLAME':  return '<span class="badge bg-secondary">EN ATTENTE RÉCLAM.</span>';
        case 'RECLAMED':         return '<span class="badge bg-success">RÉCLAMÉ</span>';
        default:                 return `<span class="badge bg-dark">${status}</span>`;
    }
};

const emptyCreateForm = { earn_amount: '', earn_status: 'PENDING', transaction_id: '' };
const emptyEditForm = { earn_amount: '', earn_status: 'PENDING', transaction_id: '' };

const Gains = () => {
    const tableRef = useRef(null);
    const [earns, setEarns] = useState([]);
    const [totalEarn, setTotalEarn] = useState(0);

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isSubmittingCreate, setIsSubmittingCreate] = useState(false);
    const [createForm, setCreateForm] = useState(emptyCreateForm);
    const [createErrors, setCreateErrors] = useState({});

    const [editingEarn, setEditingEarn] = useState(null);
    const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
    const [editForm, setEditForm] = useState(emptyEditForm);
    const [editErrors, setEditErrors] = useState({});

    const loadEarns = () => {
        ManageEarnAdmin.getClientEarns(GAINS_CLIENT_ID)
            .then((response) => {
                const meta = response.data.response;
                if (meta.status_code === 200) {
                    const data = response.data.data !== undefined ? response.data.data : meta.data;
                    setEarns(data?.earns || []);
                    setTotalEarn(data?.total_earn || 0);
                } else {
                    toast.error(meta.message || "Erreur lors du chargement des gains.");
                }
            })
            .catch(() => {
                toast.error("Erreur lors du chargement des données. Réessayer plus tard.");
            });
    };

    const openCreateModal = () => {
        setCreateForm(emptyCreateForm);
        setCreateErrors({});
        setIsCreateModalOpen(true);
    };

    const submitCreateEarn = async (event) => {
        event.preventDefault();
        setIsSubmittingCreate(true);
        setCreateErrors({});
        try {
            const payload = {
                client_id: Number(GAINS_CLIENT_ID),
                earn_amount: Number(createForm.earn_amount),
                earn_status: createForm.earn_status,
            };
            if (createForm.transaction_id !== '') {
                payload.transaction_id = Number(createForm.transaction_id);
            }

            const response = await ManageEarnAdmin.storeEarn(payload);
            const meta = response.data.response;
            if (meta.status_code === 200) {
                toast.success(meta.message || "Gain créé avec succès");
                setIsCreateModalOpen(false);
                loadEarns();
            } else if (meta.status_code === 422) {
                setCreateErrors(meta.errors || {});
                toast.error(meta.message || "Validation échouée.");
            } else {
                toast.error(meta.message || "Erreur lors de la création du gain.");
            }
        } catch (error) {
            toast.error("Erreur lors de la création du gain. Réessayer plus tard.");
        } finally {
            setIsSubmittingCreate(false);
        }
    };

    const openEditModal = (earn) => {
        setEditingEarn(earn);
        setEditForm({
            earn_amount: earn.earn_amount,
            earn_status: earn.earn_status,
            transaction_id: earn.transaction_id ?? '',
        });
        setEditErrors({});
    };

    const submitEditEarn = async (event) => {
        event.preventDefault();
        setIsSubmittingEdit(true);
        setEditErrors({});
        try {
            const payload = {
                id: editingEarn.id,
                earn_amount: Number(editForm.earn_amount),
                earn_status: editForm.earn_status,
            };
            if (editForm.transaction_id !== '') {
                payload.transaction_id = Number(editForm.transaction_id);
            }

            const response = await ManageEarnAdmin.updateEarn(payload);
            const meta = response.data.response;
            if (meta.status_code === 200) {
                toast.success(meta.message || "Gain modifié avec succès");
                setEditingEarn(null);
                loadEarns();
            } else if (meta.status_code === 422) {
                setEditErrors(meta.errors || {});
                const idError = meta.errors?.id?.[0];
                toast.error(idError || meta.message || "Validation échouée.");
            } else {
                toast.error(meta.message || "Erreur lors de la modification du gain.");
            }
        } catch (error) {
            toast.error("Erreur lors de la modification du gain. Réessayer plus tard.");
        } finally {
            setIsSubmittingEdit(false);
        }
    };

    useEffect(() => {
        const $table = $(tableRef.current);

        if (!$.fn.DataTable.isDataTable(tableRef.current)) {
            $table.DataTable({
                order: [[0, 'desc']],
                language: {
                    processing:     "Traitement en cours...",
                    search:         "Rechercher&nbsp;:",
                    lengthMenu:     "Afficher _MENU_ éléments",
                    info:           "Affichage de l'élément _START_ à _END_ sur _TOTAL_ éléments",
                    infoEmpty:      "Affichage de l'élément 0 à 0 sur 0 élément",
                    infoFiltered:   "(filtré de _MAX_ éléments au total)",
                    infoPostFix:    "",
                    loadingRecords: "Chargement en cours...",
                    zeroRecords:    "Aucun élément à afficher",
                    emptyTable:     "Aucune donnée disponible dans le tableau",
                    paginate: {
                        first:      "Premier",
                        previous:   "Précédent",
                        next:       "Suivant",
                        last:       "Dernier"
                    },
                    aria: {
                        sortAscending:  ": activer pour trier la colonne par ordre croissant",
                        sortDescending: ": activer pour trier la colonne par ordre décroissant"
                    }
                },
                data: [],
                columns: GainsTableHeader.map((header) => ({ title: header })),
            });
        }

        loadEarns();

        return () => {
            if ($.fn.DataTable.isDataTable(tableRef.current)) {
                $table.DataTable().destroy();
            }
        };
    }, []);

    useEffect(() => {
        if ($.fn.DataTable.isDataTable(tableRef.current)) {
            const table = $(tableRef.current).DataTable();
            table.clear();

            earns.forEach((earn) => {
                table.row.add([
                    earn.id,
                    earn.earn_amount,
                    earnStatusBadge(earn.earn_status),
                    earn.transaction_id ?? '-',
                    earn.created_at,
                    `<button class='btn btn-sm btn-primary edit-btn' data-id='${earn.id}'>
                        <i class='fa fa-pencil'></i> Modifier
                     </button>`
                ]);
            });
            table.draw();

            $(tableRef.current).off('click', '.edit-btn').on('click', '.edit-btn', function () {
                const id = $(this).data('id');
                const earn = earns.find((e) => e.id === id);
                if (earn) openEditModal(earn);
            });
        }
    }, [earns]);

    return (
        <Fragment>
            <Breadcrumbs mainTitle="Gains" parent="Gestion" title="Gains" />
            <Container fluid={true}>
                <Row>
                    <Col sm="12">
                        <ToastContainer />
                        <Card>
                            <CardHeader>
                                <div className="row align-items-center">
                                    <div className="col">
                                        <H4 attrH4={{ className: 'card-title mb-0' }}>
                                            Gains de l'utilisateur #{GAINS_CLIENT_ID}
                                        </H4>
                                        <p className="mb-0 text-muted">Total des gains : {totalEarn}</p>
                                    </div>
                                    <div className="col-auto">
                                        <button className="btn btn-primary" onClick={openCreateModal}>
                                            <i className="fa fa-plus me-2"></i>
                                            Créer un gain
                                        </button>
                                    </div>
                                </div>
                            </CardHeader>
                            <div className='table-responsive edit-table'>
                                <table
                                    ref={tableRef}
                                    className='display table table-striped table-vcenter text-nowrap'
                                    style={{ width: '100%' }}
                                >
                                    <thead>
                                    <tr>
                                        {GainsTableHeader.map((header, i) => (
                                            <th key={i}>{header}</th>
                                        ))}
                                    </tr>
                                    </thead>
                                    <tbody></tbody>
                                </table>
                            </div>
                        </Card>
                    </Col>
                </Row>
            </Container>

            <Modal isOpen={isCreateModalOpen} toggle={() => !isSubmittingCreate && setIsCreateModalOpen(false)} centered>
                <Form onSubmit={submitCreateEarn}>
                    <ModalHeader toggle={() => !isSubmittingCreate && setIsCreateModalOpen(false)}>
                        Créer un gain pour l'utilisateur #{GAINS_CLIENT_ID}
                    </ModalHeader>
                    <ModalBody>
                        <FormGroup>
                            <Label for="earn_amount">Montant</Label>
                            <Input
                                id="earn_amount"
                                type="number"
                                required
                                value={createForm.earn_amount}
                                onChange={(e) => setCreateForm((prev) => ({ ...prev, earn_amount: e.target.value }))}
                                invalid={!!createErrors.earn_amount}
                            />
                            {createErrors.earn_amount && <div className="text-danger small mt-1">{createErrors.earn_amount[0]}</div>}
                        </FormGroup>
                        <FormGroup>
                            <Label for="earn_status">Statut</Label>
                            <Input
                                id="earn_status"
                                type="select"
                                value={createForm.earn_status}
                                onChange={(e) => setCreateForm((prev) => ({ ...prev, earn_status: e.target.value }))}
                                invalid={!!createErrors.earn_status}
                            >
                                {EARN_STATUSES.map((status) => (
                                    <option key={status} value={status}>{status}</option>
                                ))}
                            </Input>
                            {createErrors.earn_status && <div className="text-danger small mt-1">{createErrors.earn_status[0]}</div>}
                        </FormGroup>
                        <FormGroup>
                            <Label for="transaction_id">ID de transaction (optionnel)</Label>
                            <Input
                                id="transaction_id"
                                type="number"
                                value={createForm.transaction_id}
                                onChange={(e) => setCreateForm((prev) => ({ ...prev, transaction_id: e.target.value }))}
                                invalid={!!createErrors.transaction_id}
                            />
                            {createErrors.transaction_id && <div className="text-danger small mt-1">{createErrors.transaction_id[0]}</div>}
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <button type="button" className="btn btn-secondary" disabled={isSubmittingCreate} onClick={() => setIsCreateModalOpen(false)}>
                            Annuler
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={isSubmittingCreate}>
                            {isSubmittingCreate ? <span className="spinner-border spinner-border-sm"></span> : 'Créer'}
                        </button>
                    </ModalFooter>
                </Form>
            </Modal>

            <Modal isOpen={!!editingEarn} toggle={() => !isSubmittingEdit && setEditingEarn(null)} centered>
                <Form onSubmit={submitEditEarn}>
                    <ModalHeader toggle={() => !isSubmittingEdit && setEditingEarn(null)}>
                        Modifier le gain #{editingEarn?.id}
                    </ModalHeader>
                    <ModalBody>
                        <FormGroup>
                            <Label for="edit_earn_amount">Montant</Label>
                            <Input
                                id="edit_earn_amount"
                                type="number"
                                required
                                value={editForm.earn_amount}
                                onChange={(e) => setEditForm((prev) => ({ ...prev, earn_amount: e.target.value }))}
                                invalid={!!editErrors.earn_amount}
                            />
                            {editErrors.earn_amount && <div className="text-danger small mt-1">{editErrors.earn_amount[0]}</div>}
                        </FormGroup>
                        <FormGroup>
                            <Label for="edit_earn_status">Statut</Label>
                            <Input
                                id="edit_earn_status"
                                type="select"
                                value={editForm.earn_status}
                                onChange={(e) => setEditForm((prev) => ({ ...prev, earn_status: e.target.value }))}
                                invalid={!!editErrors.earn_status}
                            >
                                {EARN_STATUSES.map((status) => (
                                    <option key={status} value={status}>{status}</option>
                                ))}
                            </Input>
                            {editErrors.earn_status && <div className="text-danger small mt-1">{editErrors.earn_status[0]}</div>}
                        </FormGroup>
                        <FormGroup>
                            <Label for="edit_transaction_id">ID de transaction (optionnel)</Label>
                            <Input
                                id="edit_transaction_id"
                                type="number"
                                value={editForm.transaction_id}
                                onChange={(e) => setEditForm((prev) => ({ ...prev, transaction_id: e.target.value }))}
                                invalid={!!editErrors.transaction_id}
                            />
                            {editErrors.transaction_id && <div className="text-danger small mt-1">{editErrors.transaction_id[0]}</div>}
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <button type="button" className="btn btn-secondary" disabled={isSubmittingEdit} onClick={() => setEditingEarn(null)}>
                            Annuler
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={isSubmittingEdit}>
                            {isSubmittingEdit ? <span className="spinner-border spinner-border-sm"></span> : 'Enregistrer'}
                        </button>
                    </ModalFooter>
                </Form>
            </Modal>
        </Fragment>
    );
};

export default Gains;
